import {Box} from '@mui/material';
import ArrModal from '../../common/arr-modal/ArrModal';
import ArrTextField from '../../common/arr-text-field/ArrTextField';
import {t} from 'i18next';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import {useForm} from 'react-hook-form';
import {
  ProjectFieldGroupForm,
  projectFieldGroupSchema,
} from './AddProjectFieldGroupModalUtils';
import {zodResolver} from '@hookform/resolvers/zod';
import {useAddProjectFieldGroup} from '../../../hooks/Projects/useProjectField';
import ArrToastNotification from '../../common/arr-toast-notification/ArrToastNotification';
import {ToastType} from '../../../shared/constants';

export type AddProjectFieldGroupModalRef = {
  open: () => void;
  close: () => void;
};

const AddProjectFieldGroupModal = forwardRef<AddProjectFieldGroupModalRef>(
  (_, ref) => {
    const [showAddProjectFieldModal, setShowAddProjectFieldModal] =
      useState(false);
    const [showToast, setShowToast] = useState(false);

    const {
      register,
      handleSubmit,
      getValues,
      reset,
      formState: {errors, isValid, isDirty},
    } = useForm<ProjectFieldGroupForm>({
      resolver: zodResolver(projectFieldGroupSchema()),
      mode: 'onChange',
      defaultValues: {
        fieldGroupLabel: '',
      },
    });

    const addProjectFieldGroupMutatiton = useAddProjectFieldGroup();
    const {
      mutate: AddProjectFieldGroupRequest,
      reset: ResetProjectFieldGroupPayload,
    } = addProjectFieldGroupMutatiton;

    const loading = useMemo(
      () => addProjectFieldGroupMutatiton.isPending,
      [addProjectFieldGroupMutatiton.isPending]
    );

    const errorMessage = useMemo(
      () =>
        (
          addProjectFieldGroupMutatiton.error?.response?.data as any
        )?.detail?.split('\n')?.[0],
      [addProjectFieldGroupMutatiton.error?.response?.data]
    );

    const onClose = useCallback(() => {
      setShowAddProjectFieldModal(false);
      reset();
    }, [reset]);

    useEffect(() => {
      if (addProjectFieldGroupMutatiton.isSuccess) {
        setShowToast(true);
        onClose();
      }
    }, [addProjectFieldGroupMutatiton.isSuccess, onClose]);

    const onOpen = useCallback(() => {
      setShowAddProjectFieldModal(true);
    }, []);

    useImperativeHandle(ref, () => ({
      open: onOpen,
      close: onClose,
    }));

    const onCloseToast = useCallback(() => {
      setShowToast(false);
    }, []);

    const handleSave = useCallback(() => {
      ResetProjectFieldGroupPayload();
      AddProjectFieldGroupRequest({
        fieldGroupLabel: getValues('fieldGroupLabel'),
      });
    }, [AddProjectFieldGroupRequest, ResetProjectFieldGroupPayload, getValues]);

    const isSaveButtonDisabled = loading || !isValid || !isDirty;

    return (
      <div data-testid={'project-field-group'}>
        <ArrModal
          showModal={showAddProjectFieldModal}
          showClose={true}
          disabledClose={loading}
          autoFocusCloseButton
          onClose={onClose}
          title={t('manageProjectField.addProjectFieldGroup.title')}
          dataTestId="add-project-field-group-modal"
          buttons={[
            {
              id: 'add-project-field-cancel',
              label: t('common.cancel'),
              onClick: onClose,
              variant: 'secondary',
              disabled: loading,
              dataTestId: 'apf-cancel-button',
            },
            {
              id: 'add-project-field-save',
              label: t('common.save'),
              onClick: handleSave,
              variant: 'primary',
              loading: loading,
              disabled: isSaveButtonDisabled,
              ariaDisabled: isSaveButtonDisabled ? 'true' : 'false',
              dataTestId: 'apf-save-button',
            },
          ]}
        >
          <Box className="productions-details-container__template-modal-container">
            <form
              onSubmit={handleSubmit(handleSave)}
              data-testid={`add-project-field-group-modal-form`}>
              <ArrTextField
                id="fieldGroupLabel"
                dataTestId="add-project-template-name"
                state={
                  errors.fieldGroupLabel || errorMessage ? 'error' : 'enabled'
                }
                label={t('manageProjectField.addProjectFieldGroup.placeholder')}
                placeholder={t(
                  'manageProjectField.addProjectFieldGroup.placeholder'
                )}
                {...register('fieldGroupLabel')}
                disabled={loading}
                autoFocus
                slotProps={{
                  input: {
                    readOnly: loading,
                  },
                }}
                inputProps={{
                  'aria-label': t('manageProjectField.fieldGroupNameAriaLabel'),
                }}
                supportText={errors.fieldGroupLabel?.message ?? errorMessage}
              />
            </form>
          </Box>
        </ArrModal>
        {showToast && (
          <ArrToastNotification
            message={t(
              'manageProjectField.addProjectFieldGroup.successMessage'
            )}
            status={[ToastType.Success]}
            onClose={onCloseToast}
            closeButtonAriaLabel={t(
              'manageProjectField.dismissNotificationAriaLable'
            )}
          />
        )}
      </div>
    );
  }
);

export {AddProjectFieldGroupModal};
